/**
 * @fileoverview The reminder email's one-tap cancel: /billing/stop?t=TOKEN&lang=es.
 *
 * Two weeks before a paused plan's billing starts again, the gardener is
 * emailed the day, the price and this link. It needs no sign-in (the token,
 * sent only to them, is what the backend trusts) and opens in the language
 * the email was in. It asks once, with one button, rather than cancel on
 * opening: mail scanners open links by themselves. The subscription then
 * ends at the end of its period, so nothing more is charged.
 *
 * Public, so outside the Layout, like the account deletion page.
 *
 * The same link is in the price notice email (35 days before a renewal
 * charged more than the last one, D17/D19): for that plan nothing is paused,
 * so the page says billing.stop.readyNoDate ("Cancel your Grow More
 * subscription, and nothing more is charged.").
 *
 * Where: the route /billing/stop (App.tsx), public, no sign-in.
 * API: GET /billing/stop?t= (readStopLink) on opening; POST /billing/stop
 * { token } (confirmStopLink) on the one button.
 * Settings: ?lang=en|es switches the app's language before the first words.
 * Words (en/es): billing.stop.*. Any failure, reading the link or stopping
 * (owner, 2026-10-17: "not a try again, but make sure we get a sentry error
 * and give them our email address to reach out to us"), is reported to
 * Sentry and says how to reach us: the standard sentence for a failure on
 * our side (a 500, no connection; D31, lib/serverFailure), else
 * billing.stop.failed, that it was not cancelled and to email us. Neither
 * offers Try again, and the button stays away after a failure.
 * Watch out: a pause "with no end" (made by hand in Stripe) has no day
 * billing starts again: the server sends its pausedUntil as null, and the
 * page says billing.stop.readyNoDate.
 *
 * @module StopSubscriptionPage
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { reportServerFailure, serverFailureText, referenceLine } from '@/shared/lib/serverFailure';
import { joinReference } from '@/shared/lib/serverFailureCore';
import { FailureText } from '@/shared/components/ServerFailure';
import { SUPPORT_EMAIL } from '@/config/tunables/shared';
import { confirmStopLink, readStopLink, type StopLink } from '@/shared/api/billing';

/** What the page shows: asking, cancelling, or the link's state; a failure with what was thrown. */
type Shown = { kind: 'loading' } | { kind: 'working'; link: StopLink } | { kind: 'link'; link: StopLink } | { kind: 'failed'; link: StopLink | null; error: unknown };

/**
 * A moment as the gardener's own day, "February 17" ("17 de febrero").
 *
 * @param iso - From the server.
 * @param language - The page's language.
 * @returns The day, or null when there is none (a pause with no end is sent as none) or it does not read.
 */
function dayOf(iso: string | null, language: string): string | null {
  if (!iso) return null;
  const at = DateTime.fromISO(iso);
  return at.isValid ? at.setLocale(language).toLocaleString({ month: 'long', day: 'numeric' }) : null;
}

/** The open page the reminder's one-tap cancel link goes to. */
export const StopSubscriptionPage: React.FC = () => {
  const [params] = useSearchParams();
  const { reactTranslate: t, i18n } = useTranslation();
  const token = params.get('t') ?? '';
  // The email's language, set before the first words are drawn.
  const [language] = React.useState(() => {
    const wanted = params.get('lang');
    if ((wanted === 'en' || wanted === 'es') && wanted !== i18n.language) i18n.changeLanguage(wanted);
    return wanted === 'en' || wanted === 'es' ? wanted : i18n.language;
  });
  const [shown, setShown] = React.useState<Shown>({ kind: 'loading' });

  React.useEffect(() => {
    let cancelled = false;
    readStopLink(token)
      .then((link) => { if (!cancelled) setShown({ kind: 'link', link }); })
      .catch((error) => { if (!cancelled) setShown({ kind: 'failed', link: null, error }); });
    return () => { cancelled = true; };
  }, [token]);

  // Every failure here is one we want to hear about: a subscription someone could not stop.
  // Reported while the failure is drawn (once per error), so its reference code exists for the first paint (E3).
  const failure = shown.kind === 'failed' ? shown.error : null;
  const failureWords = React.useMemo(() => {
    if (failure === null) return null;
    reportServerFailure(failure, 'StopSubscription');
    return serverFailureText(failure, 'StopSubscription')
      ?? joinReference(t('billing.stop.failed', { email: SUPPORT_EMAIL }), referenceLine(failure));
  }, [failure, t]);

  /** The one tap: ends the subscription at the end of its period. */
  const stop = async (link: StopLink): Promise<void> => {
    setShown({ kind: 'working', link });
    try {
      setShown({ kind: 'link', link: await confirmStopLink(token) });
    } catch (error) {
      setShown({ kind: 'failed', link, error });
    }
  };

  /**
   * The words for a link's state.
   *
   * @param link - The link as the server read it.
   */
  const words = (link: StopLink): string => {
    switch (link.status) {
      case 'ready': {
        const until = dayOf(link.pausedUntil, language);
        return until ? t('billing.stop.ready', { date: until }) : t('billing.stop.readyNoDate');
      }
      case 'stopped': {
        const ends = dayOf(link.endsOn, language);
        return ends ? t('billing.stop.stopped', { date: ends }) : t('billing.stop.stoppedNoDate');
      }
      case 'expired': return t('billing.stop.expired');
      case 'nothing': return t('billing.stop.nothing');
      default: return t('billing.stop.unknown');
    }
  };

  const link = shown.kind === 'loading' ? null : shown.link;
  const canStop = link?.status === 'ready' && shown.kind !== 'failed';

  return (
    <div className="min-h-screen w-full bg-background px-4 py-12">
      <div className="mx-auto max-w-md space-y-5 text-foreground">
        <p className="text-2xl font-semibold text-primary font-grow">Grow</p>
        <h1 className="text-xl font-semibold font-body">{t('billing.stop.title')}</h1>

        {shown.kind === 'loading' ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground"><Spinner size={18} />{t('billing.stop.loading')}</div>
        ) : (
          <>
            {link && <p className="text-sm leading-relaxed">{words(link)}</p>}
            {shown.kind === 'failed' && <p role="alert" className="text-sm text-destructive"><FailureText text={failureWords ?? ''} /></p>}
            {canStop && link && (
              <Button onClick={() => { void stop(link); }} disabled={shown.kind === 'working'}>
                {shown.kind === 'working' ? <Spinner size={16} color="currentColor" /> : null}
                <span className={shown.kind === 'working' ? 'ml-2' : ''}>{t('billing.stop.button')}</span>
              </Button>
            )}
          </>
        )}

        <p className="pt-4 text-sm">
          <a href="/subscribe" className="text-primary underline underline-offset-2">{t('billing.stop.toGrow')}</a>
        </p>
      </div>
    </div>
  );
};

export default StopSubscriptionPage;
